import {
  Alert,
  Badge,
  BadgeColor,
  Button,
  Column,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  MenuItemProps,
  Table,
} from '@odin-labs/components';
import { PlusIcon, TrashIcon } from 'components/icons';
import React from 'react';
import {
  DraftTag,
  draftTagOf,
  entriesFacingZone,
  isDuplicateName,
  Layout,
  Zone,
} from 'siteLayout';
import { draftTagBadge } from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import {
  entryColumns,
  toEntryRows,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/layoutTables';
import { NameField } from './NameField';

const classes = {
  panel: 'odin-flex odin-flex-col odin-gap-y-8',
  heading: 'odin-flex odin-items-start odin-justify-between odin-gap-3',
  kicker:
    'odin-text-xs odin-font-semibold odin-uppercase odin-tracking-wide odin-text-gray-500',
  title:
    'odin-text-2xl odin-font-semibold odin-tracking-tight odin-text-gray-900',
  // The same section heading the devices drawer uses.
  section: 'odin-space-y-4',
  sectionTitle: 'odin-text-base odin-font-medium odin-text-gray-900',
  listHeader:
    'odin-flex odin-items-center odin-justify-between odin-gap-2 odin-pt-2',
  listLabel: 'odin-text-sm odin-font-medium odin-text-gray-900',
};

export type ZoneEditorProps = {
  layout: Layout;
  zone: Zone;
  tag: DraftTag;
  readOnly: boolean;
  editingDraft: boolean;
  /** The published layout the draft is compared with, for the entries' tags. */
  effective?: Layout | null;
  /** Off when the container already titles the zone and shows its tag, as a drawer does. */
  showHeading?: boolean;
  /** The zone's Avigilon zone; absent where the revision shown says nothing about it. */
  avigilonStatus?: React.ReactNode;
  onRename: (name: string) => Promise<boolean> | void;
  onSelectEntry: (entryId: string) => void;
  /** What stopped the last Save, shown until the next attempt. */
  errors?: string[];
  /** Save has been tried: every field shows its error, touched or not. */
  submitted?: boolean;
  /** The site's access groups, for granting this zone. */
  accessGroups?: AccessGroupOption[];
  onSetAccessGroups?: (accessGroupIds: string[]) => void;
  /** Opens the drawer's assign step. */
  onAssignGroup?: () => void;
};

export type AccessGroupOption = {
  id: string;
  name: string;
  memberCount: number;
};

type GroupRow = {
  id: string;
  name: string;
  menuItems: MenuItemProps[];
};

function GroupCell({
  row,
}: {
  row: { original: GroupRow };
}): React.ReactElement {
  return (
    <span className="odin-flex odin-items-center odin-gap-2">
      <span className="odin-font-medium odin-text-gray-900">
        {row.original.name}
      </span>
    </span>
  );
}

const groupColumns: Column<GroupRow>[] = [
  { id: 'group', Header: 'Group', accessor: 'name', Cell: GroupCell },
  {
    id: 'actions',
    Header: '',
    Cell: DropdownButtonCell,
    componentProps: (row: GroupRow): DropdownButtonCellComponentProps => ({
      cellAlignment: 'right',
      menuItems: row.menuItems,
    }),
  },
];

/** The access groups granted this zone; assignment is the drawer's own step. */
function ZoneAccessGroups({
  granted,
  editing,
  onChange,
  onAssign,
}: {
  granted: AccessGroupOption[];
  editing: boolean;
  onChange: (accessGroupIds: string[]) => void;
  onAssign: () => void;
}): React.ReactElement {
  const rows = React.useMemo<GroupRow[]>(
    () =>
      granted.map((group) => ({
        id: group.id,
        name: group.name,
        menuItems: editing
          ? [
              {
                icon: TrashIcon,
                text: 'Remove',
                theme: 'danger',
                onClick: (): void =>
                  onChange(
                    granted
                      .filter((candidate) => candidate.id !== group.id)
                      .map((candidate) => candidate.id),
                  ),
              },
            ]
          : [],
      })),
    [granted, editing, onChange],
  );
  return (
    <section className={classes.section} aria-label="Groups">
      <div className={classes.listHeader}>
        <span className={classes.listLabel}>Groups</span>
        {editing && (
          <Button
            icon={PlusIcon}
            text="Assign group"
            size="sm"
            theme="white"
            onClick={onAssign}
          />
        )}
      </div>
      <Table
        headerRowClassName="odin-hidden md:odin-hidden print:odin-hidden"
        columns={groupColumns}
        data={rows}
        noResultsText="No groups granted"
        remote
        disableGlobalFilter
        disableSortBy
      />
    </section>
  );
}

export const zoneNameError = (layout: Layout, zone: Zone): string | null => {
  if (!zone.name.trim()) return 'A zone needs a name.';
  if (isDuplicateName(layout.zones, zone)) {
    return `A zone named ${zone.name.trim()} already exists in this layout.`;
  }
  return null;
};

/** The selected zone: its name and the entries that face it. Removal is a row action on the zones list. */
export function ZoneEditor({
  layout,
  zone,
  tag,
  readOnly,
  editingDraft,
  effective = null,
  showHeading = true,
  avigilonStatus,
  onRename,
  onSelectEntry,
  errors = [],
  submitted = false,
  accessGroups = [],
  onSetAccessGroups,
  onAssignGroup,
}: ZoneEditorProps): React.ReactElement {
  const badge = draftTagBadge(tag);
  const grantedIds = zone.accessGroupIds ?? [];
  const granted = grantedIds
    .map((id) => accessGroups.find((group) => group.id === id))
    .filter((group): group is AccessGroupOption => Boolean(group));
  const rows = React.useMemo(
    () =>
      toEntryRows(layout, entriesFacingZone(layout, zone.id), (entry) =>
        editingDraft
          ? draftTagBadge(draftTagOf(effective, 'entries', entry))
          : null,
      ),
    [layout, zone.id, editingDraft, effective],
  );

  return (
    <div className={classes.panel}>
      {showHeading && (
        <div className={classes.heading}>
          <div>
            <p className={classes.kicker}>Zone</p>
            <h2 className={classes.title}>{zone.name || 'Unnamed zone'}</h2>
          </div>
          {badge && (
            <Badge text={badge.text} color={badge.color as BadgeColor} />
          )}
        </div>
      )}

      {errors.length > 0 && <Alert type="danger" text={errors.join(' ')} />}

      <NameField
        value={zone.name}
        error={zoneNameError(layout, zone)}
        submitted={submitted}
        disabled={readOnly}
        onCommit={onRename}
      />

      <section className={classes.section}>
        <h3 className={classes.sectionTitle}>Entries</h3>
        <Table
          columns={entryColumns}
          data={rows}
          noResultsText="No entries face this zone."
          onRowClick={({ data: row }) => onSelectEntry(row.id)}
          remote
          disableGlobalFilter
          disableSortBy
        />
      </section>

      {/* Groups are granted to zones in M4; until then there is no section. */}
      {onSetAccessGroups && (
        <ZoneAccessGroups
          granted={granted}
          editing={!readOnly && Boolean(onSetAccessGroups)}
          onChange={(ids) => onSetAccessGroups?.(ids)}
          onAssign={() => onAssignGroup?.()}
        />
      )}

      {avigilonStatus}
    </div>
  );
}
